import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table'
import { formatCents, type AdminBillingInvoice } from '../../hooks/use-admin-billing'
import { BillingInvoiceBadge } from './BillingInvoiceBadge'

/** invoicePeriod prints the months of an invoice: one month, or the first to the last of a year. */
export function invoicePeriod(inv: Pick<AdminBillingInvoice, 'period_start' | 'period_end' | 'kind'>): string {
  const start = inv.period_start.slice(0, 7)
  if (inv.kind !== 'annual') return start
  const end = new Date(Date.UTC(Number(inv.period_end.slice(0, 4)), Number(inv.period_end.slice(5, 7)) - 2, 1))
  return `${start} → ${end.toISOString().slice(0, 7)}`
}

/** BillingInvoicesTable lists invoices with their state, each linking to its detail. */
export function BillingInvoicesTable({ invoices, showOrganization }: { invoices: AdminBillingInvoice[]; showOrganization?: boolean }) {
  const { t, i18n } = useTranslation('admin')
  return (
    <Table data-testid="billing-invoices-table">
      <TableHeader>
        <TableRow>
          {showOrganization && <TableHead>{t('billing.invoices.column.organization')}</TableHead>}
          <TableHead>{t('billing.invoices.column.number')}</TableHead>
          <TableHead>{t('billing.invoices.column.kind')}</TableHead>
          <TableHead>{t('billing.invoices.column.period')}</TableHead>
          <TableHead>{t('billing.invoices.column.due_on')}</TableHead>
          <TableHead className="text-right">{t('billing.invoices.column.total')}</TableHead>
          <TableHead className="text-right">{t('billing.invoices.column.remaining')}</TableHead>
          <TableHead>{t('billing.invoices.column.state')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {invoices.map((inv) => (
          <TableRow key={inv.uid} data-testid={`billing-invoice-row-${inv.uid}`}>
            {showOrganization && <TableCell>{inv.organization_name || inv.organization_code}</TableCell>}
            <TableCell>
              <Link to="/admin/billing/invoices/$uid" params={{ uid: inv.uid }} className="text-primary hover:underline" data-testid={`billing-invoice-link-${inv.uid}`}>
                {inv.number ?? t('billing.invoices.no_number')}
              </Link>
            </TableCell>
            <TableCell>{t(`billing.invoices.kind.${inv.kind}`)}</TableCell>
            <TableCell className="tabular-nums">{invoicePeriod(inv)}</TableCell>
            <TableCell className="tabular-nums">{inv.due_on ?? '—'}</TableCell>
            <TableCell className="text-right tabular-nums">{formatCents(inv.total_cents, i18n.language)}</TableCell>
            <TableCell className="text-right tabular-nums">
              {inv.status === 'issued' ? formatCents(inv.remaining_cents, i18n.language) : '—'}
            </TableCell>
            <TableCell>
              <BillingInvoiceBadge state={inv.payment_state} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
